import React from 'react';
import { useCommodities, useCurrencies, useIndices } from './useMarketData'; // Adjust path if needed

// --- HELPER COMPONENT FOR ROWS ---
const DataRow = ({ label, subLabel, price, change, isPositive, theme }) => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'space-between', 
    alignItems: 'center',
    padding: '0.75rem',
    background: 'rgba(255,255,255,0.03)',
    borderRadius: '8px',
    border: '1px solid rgba(255,255,255,0.05)',
    marginBottom: '0.5rem'
  }}>
    <div>
      <span style={{ fontWeight: 600, color: theme.textPrimary, fontSize: '0.9rem', display: 'block' }}>
        {label}
      </span>
      <span style={{ fontSize: '0.7rem', color: theme.textSecondary }}>
        {subLabel}
      </span>
    </div>
    <div style={{ textAlign: 'right' }}>
      <div style={{ fontFamily: 'monospace', fontSize: '0.9rem', color: theme.textPrimary }}>
        {price}
      </div>
      <div style={{ 
        fontSize: '0.75rem', 
        fontWeight: 600, 
        color: isPositive ? '#4ade80' : '#ef4444' 
      }}>
        {change}
      </div>
    </div>
  </div>
);

// --- 1. COMMODITIES CARD ---
export const CommoditiesCard = ({ theme, limit }) => {
  const { data, loading } = useCommodities();

  if (loading) return <div style={{ height: '300px', background: 'rgba(255,255,255,0.05)', borderRadius: '12px' }} />;
  if (!data) return null;

  // Sort by volatility (absolute change)
  const items = [...data]
    .sort((a, b) => Math.abs(b.rate) - Math.abs(a.rate))
    .slice(0, limit || 8);

  return (
    <div style={{
      background: theme.cardBg,
      borderRadius: '12px',
      padding: '1.5rem',
      border: `1px solid ${theme.border}`,
      backdropFilter: 'blur(10px)',
      height: '100%'
    }}>
      <h3 style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#D4AF37', marginBottom: '1rem' }}>
        🛢️ Commodities
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {items.map((item, i) => (
          <DataRow 
            key={i}
            theme={theme}
            label={item.name}
            subLabel={item.symbol}
            price={`$${item.last_quote?.toFixed(2) || item.close?.toFixed(2)}`}
            change={`${item.isIncrement ? '+' : ''}${item.rate?.toFixed(2)}%`}
            isPositive={item.isIncrement}
          />
        ))}
      </div>
    </div>
  );
};

// --- 2. CURRENCIES CARD ---
export const CurrenciesCard = ({ theme, limit }) => {
  const { data, loading } = useCurrencies();

  if (loading) return <div style={{ height: '300px', background: 'rgba(255,255,255,0.05)', borderRadius: '12px' }} />;
  if (!data) return null;

  const items = [...data]
    .filter(c => !c.symbol.includes('USDT')) // Clean list
    .slice(0, limit || 8);

  return (
    <div style={{
      background: theme.cardBg,
      borderRadius: '12px',
      padding: '1.5rem',
      border: `1px solid ${theme.border}`,
      backdropFilter: 'blur(10px)',
      height: '100%'
    }}>
      <h3 style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#D4AF37', marginBottom: '1rem' }}>
        💱 Moedas
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {items.map((item, i) => (
          <DataRow 
            key={i}
            theme={theme}
            label={item.symbol}
            subLabel={item.name}
            price={`R$ ${item.last_quote?.toFixed(4)}`}
            change={`${item.isIncrement ? '+' : ''}${item.rate?.toFixed(2)}%`}
            isPositive={item.isIncrement}
          />
        ))}
      </div>
    </div>
  );
};

// --- 3. INDICES CARD ---
export const IndicesCard = ({ theme, limit }) => {
  const { data, loading } = useIndices();

  if (loading) return <div style={{ height: '300px', background: 'rgba(255,255,255,0.05)', borderRadius: '12px' }} />;
  if (!data) return null;

  // Priority sort: Global Majors first
  const priority = ['IBOV', '^BVSP', 'SPX', '^GSPC', 'DJI', 'IXIC', 'UKX', 'DAX', 'N225'];
  
  const items = [...data]
    .sort((a, b) => {
        const aP = priority.indexOf(a.symbol);
        const bP = priority.indexOf(b.symbol);
        // If both are in priority list, sort by priority index
        if (aP !== -1 && bP !== -1) return aP - bP;
        // If only A is in priority, A comes first
        if (aP !== -1) return -1;
        // If only B is in priority, B comes first
        if (bP !== -1) return 1;
        // Otherwise sort by volatility
        return Math.abs(b.rate) - Math.abs(a.rate);
    })
    .slice(0, limit || 8);

  return (
    <div style={{
      background: theme.cardBg,
      borderRadius: '12px',
      padding: '1.5rem',
      border: `1px solid ${theme.border}`,
      backdropFilter: 'blur(10px)',
      height: '100%'
    }}>
      <h3 style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#D4AF37', marginBottom: '1rem' }}>
        📊 Índices
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {items.map((item, i) => (
          <DataRow 
            key={i}
            theme={theme}
            label={item.symbol}
            subLabel={item.name}
            price={item.last_quote?.toLocaleString()}
            change={`${item.isIncrement ? '+' : ''}${item.rate?.toFixed(2)}%`}
            isPositive={item.isIncrement}
          />
        ))}
      </div>
    </div>
  );
};

// --- 4. KEY INDICATORS BAR (HEADER) ---
// Restored fully to fix the disappearance issue
export const KeyIndicatorsBar = ({ theme }) => {
  const { data: indices } = useIndices();
  const { data: currencies } = useCurrencies();
  const { data: commodities } = useCommodities();

  const findAsset = (list, searchProps) => {
    if (!list) return null;
    return list.find(item => searchProps.includes(item.symbol) || searchProps.includes(item.name));
  };

  const assets = [
    { 
      label: 'IBOV', 
      item: findAsset(indices, ['^BVSP', 'IBOV', 'Ibovespa']),
      prefix: ''
    },
    { 
      label: 'DÓLAR', 
      item: findAsset(currencies, ['BRL', 'USD/BRL', 'Dólar']),
      prefix: 'R$ '
    },
    { 
      label: 'OURO', 
      item: findAsset(commodities, ['GC=F', 'Gold', 'Ouro']),
      prefix: 'US$ '
    },
    { 
      label: 'BRENT', 
      item: findAsset(commodities, ['BZ=F', 'Brent', 'Petróleo Brent']),
      prefix: 'US$ '
    }
  ];

  return (
    <div style={{ display: 'flex', gap: '2rem', alignItems: 'center' }}>
      {assets.map((asset, i) => {
        if (!asset.item) return null;
        
        const price = asset.item.last_quote || asset.item.close || 0;
        const change = asset.item.rate || asset.item.change || 0;
        const isPositive = (asset.item.isIncrement !== undefined) ? asset.item.isIncrement : (change >= 0);

        return (
          <div key={i} style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ 
              fontSize: '0.7rem', 
              color: theme.textSecondary, 
              fontWeight: 600, 
              letterSpacing: '0.5px' 
            }}>
              {asset.label}
            </span>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
              <span style={{ 
                fontSize: '0.95rem', 
                fontWeight: 'bold', 
                color: theme.textPrimary 
              }}>
                {asset.prefix}{price.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
              <span style={{ 
                fontSize: '0.8rem', 
                fontWeight: 'bold', 
                color: isPositive ? '#4ade80' : '#ef4444' 
              }}>
                {isPositive ? '▲' : '▼'} {Math.abs(change).toFixed(2)}%
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};